import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme as useWidgetTheme } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../Store";
import { allMessagesStore, getLanguageSelector } from "../../Store/Slices/socket/AllMessagesSlice";
import { botInfoStore, setUserExpanded } from "../../Store/Slices/socket/BotInfoSlice";
import { deviceSizeStore } from "../../Store/Slices/socket/DeviceSizeSlice";
import {
  CUSTOM_HEIGHT_BOUNDS,
  CUSTOM_WIDTH_BOUNDS,
  clampCustomSize,
  resolveDockedFrameWidth,
  Surface,
  WIDGET_SIZE_PRESETS,
} from "../../Models/ChatbotConfiguration";
import { StyledChatUIBoxWrap } from "../Styles/StyledScreens";
import AppChatScreenWrapper from "../appComponents/UtilsComponents/AppChatScreenWrapper";
import useMessagesData from "../hooks/useMessagesData";
import { PreviewType } from "../utils/Enum";
import AppInputHandler from "./AppInputHandler";
import Header from "./Header";
import WidgetCopyright from "./WidgetCopyright";
import AppChatLoadingScreen from "../appComponents/UtilsComponents/AppChatLoadingScreen";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";

interface Props {
  isFullScreen?: boolean;
  isMobileView?: boolean;
  surface?: Surface;
}

const MainUI: React.FC<Props> = ({ isFullScreen,isMobileView,surface }) => {
  const dispatch = useAppDispatch();
  const { chatOpened, widgetPreviewType } =
    useAppSelector(botInfoStore);

  const wasChatOpenedRef = useRef(chatOpened);
  useLayoutEffect(() => {
    if (chatOpened && !wasChatOpenedRef.current) {
      dispatch(setUserExpanded(false));
    }
    wasChatOpenedRef.current = chatOpened;
  }, [chatOpened, dispatch]);
  const deviceSize = useAppSelector(deviceSizeStore);
  const { height, width } = deviceSize;
  const { sessionId, allMessages, botLanguage } =
    useAppSelector(allMessagesStore);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [disableStartScreen, setDisableStartScreen] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const languageSelector = useAppSelector(getLanguageSelector);
  const hasFetchedRef = useRef(false);
  // const [disableHeaderSelection, setDisableHeaderSelection] = useState<boolean>(false);
  //custom hooks
  // console.log("MainUI Rendered",allMessages);
  const {
    getNewMessages,
    getHistoryMessages,
    loading,
    getNewMessagesForDirectLivechat,
    requestParams,
  } = useMessagesData();

  const { botConfig } = useAppSelector(useBotConfigStore);
  const conversationLimit = useMemo(
    () => botConfig?.config?.conversation,
    [botConfig]
  );
  const directLiveChatEnabled = useMemo(
    () =>
      botConfig?.chatbot?.hasOwnProperty("bot_enabled")
        ? !botConfig.chatbot.bot_enabled
        : false,
    [botConfig]
  );

  // ********************************************************

  // ─── REQUEST DATA EVENTS ──────────────────────────────────────────────────────────────

  // ********************************************************
  const isOldVisitor = useMemo(
    () => sessionId?.length > 0 && widgetPreviewType !== PreviewType.getaWidgetPreview,
    [sessionId, widgetPreviewType]
  );
  
  // Reset fetch flag when sessionId changes to a valid value (not null/empty)
  // Don't reset when clearing session (null) to prevent duplicate fetches
  useEffect(() => {
    if (sessionId?.length > 0) {
      // console.log("[MainUI] Resetting hasFetchedRef due to new sessionId:", sessionId);
      hasFetchedRef.current = false;
    }
  }, [sessionId]);
  
  const getMessages = () => {
    // console.log("[MainUI getMessages] Called. isOldVisitor:", isOldVisitor, "sessionId:", sessionId, "requestParams:", requestParams);
    // const isOldVisitor =
    //   sessionId?.length > 0 &&
    //   widgetPreviewType !== PreviewType.getaWidgetPreview;

    if (isOldVisitor) {
      getHistoryMessages(requestParams);
    } else if (directLiveChatEnabled) {
      getNewMessagesForDirectLivechat({
        ...requestParams,
        session_id: null,
        bot_enabled: false,
        language: botLanguage,
      });
    } else {
      //get new messgaes
      getNewMessages({ ...requestParams, language:botLanguage });
    }
  };

  const onFormSubmitted = useCallback(() => {
    setFormSubmitted(true);
  }, []);

  // useEffect(() => {
  //   setFormSubmitted(false);
  // }, []);

  useEffect(() => {
    
    if (!chatOpened) return;
    if (!conversationLimit?.enabled) return;
    if (allMessages?.length > 0) return;
    if (hasFetchedRef.current) return; // Prevent duplicate fetches
    
    if (isOldVisitor) {
      // console.log("[MainUI useEffect] Fetching for old visitor with session:", sessionId);
      hasFetchedRef.current = true;
      getMessages();
      setShowStartScreen(false);
      setFormSubmitted(true);
      return;
    }
    if (!languageSelector) {
      // console.log("[MainUI useEffect] Fetching for new visitor");
      hasFetchedRef.current = true;
      getMessages();
    }
  }, [
    chatOpened,
    // formSubmitted,
    // botConfig?.config?.start_screen,
    botLanguage,
    languageSelector,
    allMessages,
    conversationLimit,
    isOldVisitor,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same resolved flag the styling uses (see WidgetApp). Bots not on the
  // redesign keep the previous hard-coded 376x610 frame.
  const widgetTheme: any = useWidgetTheme();
  const isNewUi = !!widgetTheme?.industry_style?.enabled;

  const widgetSizePreset = useMemo(() => {
    if (!isNewUi) return null;
    const size = (botConfig?.config?.widget_size ?? "standard") as keyof typeof WIDGET_SIZE_PRESETS;
    return WIDGET_SIZE_PRESETS[size] ?? WIDGET_SIZE_PRESETS.standard;
  }, [botConfig, isNewUi]);

  // Master applied no bounds to these; only the redesign clamps them.
  const resolveCustomSize = (value: unknown, bounds: { min: number; max: number }) =>
    isNewUi ? clampCustomSize(value, bounds) : (Number(value) || null);

  const frameWidth = useMemo(() => {
    // Expanded is sized by SURFACE_PANEL_WIDTH/HEIGHT in StyledChatUIBoxWrap,
    // which ignores devicewidth — so this value is unused on that surface. The
    // store holds the docked size (see Home), not the expanded one.
    if (surface === "expanded") return width;
    return resolveDockedFrameWidth({
      preset: widgetSizePreset,
      customWidth: resolveCustomSize(
        botConfig?.config?.custom_width,
        CUSTOM_WIDTH_BOUNDS
      ),
      storeWidth: width,
    });
   }, [width,botConfig,surface,widgetSizePreset]);

  const frameHeight = useMemo(() => {
    const autoHeight = botConfig?.config?.auto_height ?? false;
    const modifiedFinalValue = height - (botConfig?.config?.auto_height_limiting_number ?? 0);
    // If window height is less than 620px, make frameHeight equal to window height
    if (height <= 620) {
      return height;
    }

    if (!isNewUi) {
      // Master had no custom-height branch and a fixed 610 ceiling.
      const legacyMax = autoHeight ? modifiedFinalValue : 610;
      return legacyMax ?? 610;
    }

    const customHeight = resolveCustomSize(
      botConfig?.config?.custom_height,
      CUSTOM_HEIGHT_BOUNDS
    );
    if (customHeight) {
      return customHeight;
    }

    // Same as the width above: expanded takes what the host gives us, not the
    // docked preset.
    if (surface === "expanded") {
      return height;
    }

    const presetHeight = widgetSizePreset ? widgetSizePreset.height + 78 : 610;

    const maxHeight = autoHeight ? modifiedFinalValue : presetHeight;
    return maxHeight ?? 610;
   }, [height,botConfig, widgetSizePreset, surface]);

  return (
    <StyledChatUIBoxWrap
      chatopened={chatOpened ? 1 : 0}
      devicewidth={frameWidth}
      deviceheight={frameHeight - 78} // 78 is the height of the header
      isFullScreen={isFullScreen}
      isMobileView={isMobileView}
      surface={surface}
    >
      <Header onFormSubmitted={onFormSubmitted} setShowStartScreen={setShowStartScreen} />
      <AppChatLoadingScreen loading={loading} />
      <AppChatScreenWrapper onFormSubmitted={onFormSubmitted} setDisableStartScreen={setDisableStartScreen} setShowStartScreen={setShowStartScreen} showStartScreen={showStartScreen}   />
      <AppInputHandler />
      {(botConfig?.config?.show_branding === undefined ||
        botConfig?.config?.show_branding === true) && <WidgetCopyright />}
    </StyledChatUIBoxWrap>
  );
};

export default MainUI;